import { CloudRain } from "lucide-react";
import { useState, useEffect } from "react";
import { handlePostRequest } from "../../hooks/api";
import { errorToast } from "../../utils/toasters";
import GenericCardSkeleton from "../skeletons/GenericCardSkeleton";

export default function WeatherCard({
  backgroundImg = "/images/cloudy-background.png",
  weatherIcon = <CloudRain className="h-5 w-5 text-indigo-400" />,
  dayIcon = "/images/sun-icon.png",
  nightIcon = "/images/moon-icon.png"
}) {
  const [weatherData, setWeatherData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      const geohash = localStorage.getItem("geohash") || "9v6m";
      const requestData = {
        lat: "30.2672",
        lang: "-97.7431",
        geohash: geohash
      };

      try {
        const response = await handlePostRequest(
          "/news/wetherReport",
          requestData,
          {},
          false
        );
        if (response && response.data) {
          setWeatherData(response);
        }
      } catch (error) {
        errorToast(
          "" + (error.response?.data?.message ?? error.data?.message ?? error),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true
          }
        );
      } finally {
        setIsProcessing(false);
      }
    };

    fetchWeather();
  }, []);

  const maxtemp = weatherData?.data?.maxtemp || 0;
  const mintemp = weatherData?.data?.mintemp || 0;
  const trending = weatherData?.data?.trending || "Weather forecast";
  const algtrending =
    weatherData?.data?.algtrending ||
    "Weather details unavailable at the moment.";

  if (isProcessing) {
    return <GenericCardSkeleton />;
  }

  return (
    <div className="w-full">
      <div className="rounded-2xl border overflow-hidden shadow-sm bg-gray-100">
        <div className="relative p-4 flex justify-between items-center">
          <div className="absolute inset-0">
            <div className="relative w-full h-full">
              <img
                src={backgroundImg}
                alt="Background"
                className="object-cover rounded-t-2xl w-full h-full absolute"
                style={{
                  filter: "blur(1px) brightness(0.9)",
                  transform: "scale(1.05)"
                }}
              />
            </div>
          </div>

          <div className="relative z-10 flex justify-between items-center w-full">
            <div className="flex items-center overflow-x-hidden">
              <div className="h-full flex-shrink-0">
                <img
                  src={dayIcon}
                  alt="Day icon"
                  className="h-full object-contain rounded-full"
                />
              </div>

              <div className="ml-1 flex flex-col justify-center">
                <div className="text-gray-800 font-medium font-afacad">
                  At Day
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xl font-bold font-fraunces">
                    {maxtemp}°
                  </span>
                  <span className="text-sm text-gray-600 font-afacad ">
                    Highest
                  </span>
                </div>
              </div>
            </div>

            <div className="h-16 w-px bg-gray-300" />

            <div className="flex items-center overflow-x-hidden">
              <div className="ml-4 flex flex-col justify-center">
                <div className="text-gray-800 font-medium font-afacad">
                  At Night
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold font-fraunces">
                    {mintemp}°
                  </span>
                  <span className="text-sm text-gray-600 font-afacad ">
                    Lowest
                  </span>
                </div>
              </div>

              <div className="h-full flex-shrink-0">
                <img
                  src={nightIcon}
                  alt="Night icon"
                  className="h-full object-contain rounded-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white">
          <div className="flex items-center gap-2 mb-2">
            {weatherIcon}
            <span className="font-medium text-gray-800 font-afacad">
              {trending}
            </span>
          </div>
          <p className="text-gray-600 text-sm font-afacad">{algtrending}</p>
        </div>
      </div>
    </div>
  );
}
